import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import { useFreecodeContextData, useFreecodeCreatorContext } from 'hooks/useContext';
import services from 'api/services';
import { RoundIconButton } from 'components/Button/IconButton';
import { HiddenFileInput } from 'components/FreecodePage/FreecodeTabs/Creator/styles';
import { buttonIds } from 'components/FreecodePage/FreecodeLayout/FreecodeLayoutConstants';
import { useExportZip } from './exportFreecode';
const { FreecodeCreatorService } = services;

export default function CreatorButtons() {
  const navigate = useNavigate();

  const { save, saving } = useFreecodeCreatorContext();
  const { test, testImpl } = useFreecodeContextData();

  const [exportZip, exporting] = useExportZip();

  const [importing, setImporting] = useState(false);

  const hiddenImportElement = useRef(null);

  const importZip = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return toast.error('No file selected');
    if (file.type !== 'application/zip') return toast.error('Can only import zip files');

    if (importing) return toast.error('Already Importing');
    setImporting(true);

    const doImport = async () => {
      try {
        await new Promise((resolve, reject) => {
          try {
            const reader = new FileReader();

            reader.onload = async () => {
              try {
                let response = await FreecodeCreatorService.importFreecode(test._id, testImpl?._id, {
                  zipString: reader.result,
                });

                if (response.error) return reject(response.error.message || 'Upload returned an error');
                if (!response.test || !response.testImpl) return reject('Import response missing data');

                toast.success('Import Done');
                resolve();
              } catch (e) {
                reject(e);
              }
            };

            reader.onerror = () => reject('Could Not Read File');
            reader.readAsDataURL(file);
          } catch (e) {
            reject(e);
          }
        });
        // Force reload the page to make sure import is loaded correctly
        navigate(0);
      } catch (e) {
        toast.error(typeof e === 'string' ? e : e?.message || 'Could not import');
      } finally {
        setImporting(false);
      }
    };
    doImport();
  };

  return (
    <>
      <RoundIconButton
        id={buttonIds.saveButton}
        title="Save you current work"
        onClick={() => save()}
        icon={saving ? 'fa fa-spinner' : 'fa-regular fa-floppy-disk'}
        spinner={saving}
      />
      <RoundIconButton
        id={buttonIds.exportButton}
        title="Export Freecode Zip"
        onClick={exportZip}
        icon={exporting ? 'fa fa-spinner' : 'fa fa-download'}
        spinner={exporting}
      />
      <RoundIconButton
        id={buttonIds.importButton}
        title="Import Freecode Zip"
        onClick={() => hiddenImportElement.current?.click?.()}
        icon={importing ? 'fa fa-spinner' : 'fa fa-upload'}
        spinner={importing}
      />
      <HiddenFileInput ref={hiddenImportElement} onChange={importZip}></HiddenFileInput>
      <RoundIconButton
        id={buttonIds.wikiLink}
        icon="fa-brands fa-wikipedia-w"
        title="Go to the Wiki"
        onClick={() => window.open('https://wiki.futureskill.com/index.php/Freecode_creator', '_blank')}
      />
    </>
  );
}
